import React, { useState, useRef, useEffect } from 'react';

/**
 * StatusColorIndicator
 * 
 * An "ℹ" info button that shows a tooltip/popover explaining the row color coding
 * used for student status indicators across tables, export, and print pages.
 */

interface StatusColorIndicatorProps {
  /** If true, renders a print-friendly static legend instead of a tooltip */
  printMode?: boolean;
}

const COLOR_INDICATORS = [
  { color: '#fde047', label: 'Inactive Student', className: 'bg-yellow-300' },
  { color: 'transparent', isStrike: true, label: 'Deleted Student', className: 'line-through text-gray-400' },
  { color: '#ffffff', label: 'Active Student', className: 'bg-white' },
];

/** Print-friendly static legend (always visible, no tooltip) */
const PrintLegend = () => (
  <div className="flex items-center gap-4 text-sm py-1 print:flex" style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}>
    {COLOR_INDICATORS.map((item) => (
      <div key={item.label} className="flex items-center gap-1">
        {item.isStrike ? (
          <span className="flex items-center justify-center w-4 h-4 text-sm font-bold line-through text-gray-400 px-1">-</span>
        ) : (
          <span
            className="inline-block w-4 h-4 border border-gray-400 rounded-sm"
            style={{ backgroundColor: item.color, printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } as React.CSSProperties}
          />
        )}
        <span className={`text-base ${item.isStrike ? 'line-through text-gray-400' : ''}`}>{item.label}</span>
      </div>
    ))}
  </div>
);

/** Interactive tooltip version for table UI */
const StatusColorIndicator: React.FC<StatusColorIndicatorProps> = ({ printMode = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (printMode) {
    return <PrintLegend />;
  }

  return (
    <div className="relative inline-block print:hidden">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-blue-500 bg-white text-blue-500 font-bold text-sm hover:bg-blue-50 transition-colors duration-150 cursor-pointer shadow-sm"
        title="Color Indicator Info"
        type="button"
        id="status-color-info-btn"
      >
        i
      </button>
      {isOpen && (
        <div
          ref={tooltipRef}
          className="absolute right-0 top-10 z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-4 min-w-[220px] animate-fadeIn"
          style={{ animation: 'fadeIn 0.15s ease-in-out' }}
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-sm text-gray-700">Row Color Indicators</h4>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none cursor-pointer"
              type="button"
            >
              ×
            </button>
          </div>
          <div className="space-y-2">
            {COLOR_INDICATORS.map((item) => (
              <div key={item.label} className="flex items-center gap-3">
                {item.isStrike ? (
                  <span className="flex items-center justify-center w-5 h-5 text-sm font-bold line-through text-gray-400 flex-shrink-0">-</span>
                ) : (
                  <span
                    className="inline-block w-5 h-5 rounded border border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                )}
                <span className={`text-sm font-medium ${item.isStrike ? 'line-through text-gray-400' : 'text-gray-600'}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { StatusColorIndicator, PrintLegend };
export default StatusColorIndicator;
